class DetailsAccordionAnimation {
  constructor(details) {
    this.details = details;
    this.summary = details.querySelector('summary');
    this.content = details.querySelector('[data-details-accordion-content]');
    this.isAnimating = false;
    this.finishTimer = null;

    if (!(this.summary instanceof HTMLElement) || !(this.content instanceof HTMLElement)) {
      return;
    }

    this.handleClick = this.handleClick.bind(this);
    this.handleToggle = this.handleToggle.bind(this);

    this.syncState();
    this.summary.addEventListener('click', this.handleClick);
    this.details.addEventListener('toggle', this.handleToggle);
  }

  handleClick(event) {
    if (this.isAnimating) {
      event.preventDefault();
      return;
    }

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    event.preventDefault();

    if (this.details.open) {
      this.close();
      return;
    }

    this.open();
  }

  handleToggle() {
    if (this.isAnimating) {
      return;
    }

    this.syncState();
  }

  syncState() {
    window.clearTimeout(this.finishTimer);

    if (this.details.open) {
      this.content.style.height = 'auto';
      this.content.style.opacity = '1';
      this.content.style.overflow = 'visible';
      return;
    }

    this.content.style.height = '0px';
    this.content.style.opacity = '0';
    this.content.style.overflow = 'hidden';
  }

  getDuration() {
    const computedStyle = window.getComputedStyle(this.content);
    const duration = computedStyle.transitionDuration.split(',')[0]?.trim() || '0s';
    const delay = computedStyle.transitionDelay.split(',')[0]?.trim() || '0s';

    return this.parseTime(duration) + this.parseTime(delay);
  }

  parseTime(value) {
    if (value.endsWith('ms')) {
      return Number.parseFloat(value);
    }

    if (value.endsWith('s')) {
      return Number.parseFloat(value) * 1000;
    }

    return 0;
  }

  waitForFinish(callback) {
    const duration = this.getDuration();
    const complete = () => {
      if (!this.isAnimating) {
        return;
      }

      this.isAnimating = false;
      callback();
    };

    if (duration === 0) {
      requestAnimationFrame(complete);
      return;
    }

    this.finishTimer = window.setTimeout(complete, duration + 50);
  }

  open() {
    this.isAnimating = true;
    this.details.removeAttribute('data-details-accordion-closing');
    this.details.open = true;
    this.content.style.overflow = 'hidden';
    this.content.style.height = '0px';
    this.content.style.opacity = '0';
    this.content.getBoundingClientRect();

    requestAnimationFrame(() => {
      this.content.style.height = `${this.content.scrollHeight}px`;
      this.content.style.opacity = '1';
      this.waitForFinish(() => {
        this.content.style.height = 'auto';
        this.content.style.opacity = '1';
        this.content.style.overflow = 'visible';
      });
    });
  }

  close() {
    this.isAnimating = true;
    this.details.setAttribute('data-details-accordion-closing', '');
    this.content.style.overflow = 'hidden';
    this.content.style.height = `${this.content.scrollHeight}px`;
    this.content.style.opacity = '1';
    this.content.getBoundingClientRect();

    requestAnimationFrame(() => {
      this.content.style.height = '0px';
      this.content.style.opacity = '0';
      this.waitForFinish(() => {
        this.details.open = false;
        this.details.removeAttribute('data-details-accordion-closing');
        this.content.style.height = '0px';
        this.content.style.opacity = '0';
        this.content.style.overflow = 'hidden';
      });
    });
  }
}

const detailsAccordions = new WeakMap();

const initDetailsAccordions = (root = document) => {
  root.querySelectorAll('[data-details-accordion]').forEach((details) => {
    if (!(details instanceof HTMLDetailsElement) || detailsAccordions.has(details)) {
      return;
    }

    detailsAccordions.set(details, new DetailsAccordionAnimation(details));
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initDetailsAccordions(), { once: true });
} else {
  initDetailsAccordions();
}

document.addEventListener('shopify:section:load', (event) => {
  if (!(event.target instanceof HTMLElement)) {
    return;
  }

  initDetailsAccordions(event.target);
});
